import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { wikiApi, type ReactionsDto } from '../lib/wikiApi';

const EMOJI = ['👍', '❤️', '🔥', '👏', '🤔'] as const;

/**
 * Реакции под статьёй / новостью (фаза 1.2): пять эмодзи, счётчики общие,
 * своя — подсвечена. Optimistic toggle, откат при ошибке.
 */
export function ReactionBar({ targetType, targetId, compact }: { targetType: 'article' | 'news'; targetId: string; compact?: boolean }): JSX.Element {
  const qc = useQueryClient();
  const key = ['wiki', 'reactions', targetType, targetId];
  const q = useQuery({ queryKey: key, queryFn: () => wikiApi.getReactions(targetType, targetId), staleTime: 15_000 });
  const mut = useMutation({
    mutationFn: (emoji: string) => wikiApi.toggleReaction(targetType, targetId, emoji),
    onMutate: async (emoji) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ReactionsDto>(key);
      if (prev) {
        const mine = prev.mine.includes(emoji);
        qc.setQueryData<ReactionsDto>(key, {
          counts: { ...prev.counts, [emoji]: Math.max(0, (prev.counts[emoji] ?? 0) + (mine ? -1 : 1)) },
          mine: mine ? prev.mine.filter((e) => e !== emoji) : [...prev.mine, emoji],
        });
      }
      return { prev };
    },
    onError: (e: Error, _emoji, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      message.error(e.message);
    },
    onSuccess: (data) => qc.setQueryData(key, data),
  });
  const data = q.data;
  return (
    <div className="fk-reactions" data-compact={compact ? 'true' : undefined} role="group" aria-label="Реакции">
      {EMOJI.map((e) => {
        const n = data?.counts[e] ?? 0;
        const mine = data?.mine.includes(e) ?? false;
        if (compact && n === 0 && !mine) return null;
        return (
          <button
            key={e}
            type="button"
            className="fk-reaction"
            data-on={mine ? 'true' : undefined}
            onClick={() => mut.mutate(e)}
            disabled={mut.isPending}
            title={mine ? 'Убрать реакцию' : 'Поставить реакцию'}
          >
            <span aria-hidden>{e}</span>
            {n > 0 && <span className="fk-reaction-n">{n}</span>}
          </button>
        );
      })}
      {compact && (data ? Object.values(data.counts).every((n) => n === 0) : true) && (
        <button type="button" className="fk-reaction" onClick={() => mut.mutate('👍')} disabled={mut.isPending} title="Поставить реакцию">
          <span aria-hidden>👍</span>
        </button>
      )}
    </div>
  );
}
